<template>
  <div class="flex flex-col">
    <slot></slot>
    <button-group>
      <filter-button
        @click="showPartySelection"
        :active="checkedParties.length > 0"
        v-if="parties.length > 0"
      >
        {{ $t("components.buttons.sorting_button.button_party") }}
      </filter-button>
      <button
        class="button-large text-chplus-s"
        :class="[
          {
            'light-theme': lightTheme,
            active: sortState.sortBy === 'matchPercent'
          }
        ]"
        @click="sortBy('matchPercent', true)"
      >
        <template>
          <icon-sort-increase
            v-show="
              sortState.sortBy === 'matchPercent' && sortState.reverse === false
            "
          />
          <icon-sort-decrease
            v-show="
              sortState.sortBy !== 'matchPercent' || sortState.reverse === true
            "
          />
        </template>
        {{ $t("components.buttons.sorting_button.button_match") }}
      </button>
      <button
        v-if="showCustomButton"
        class="button-large text-chplus-s"
        :class="[
          {
            'light-theme': lightTheme,
            active: sortState.sortBy === 'matchRank'
          }
        ]"
        @click="sortBy('matchRank')"
      >
        <template>
          <icon-sort-increase
            v-show="
              sortState.sortBy !== 'matchRank' || sortState.reverse === false
            "
          />
          <icon-sort-decrease
            v-show="
              sortState.sortBy === 'matchRank' && sortState.reverse === true
            "
          />
        </template>
        {{ $t("components.buttons.sorting_button.button_ranking") }}
      </button>
      <button
        v-else
        class="button-large text-chplus-s"
        :class="[
          {
            'light-theme': lightTheme,
            active: sortState.sortBy === 'birthYear'
          }
        ]"
        @click="sortBy('birthYear', true)"
      >
        <template>
          <icon-sort-increase
            v-show="
              sortState.sortBy !== 'birthYear' || sortState.reverse === true
            "
          />
          <icon-sort-decrease
            v-show="
              sortState.sortBy === 'birthYear' && sortState.reverse === false
            "
          />
        </template>
        {{ $t("components.buttons.sorting_button.button_age") }}
      </button>
      <button
        v-if="showSearchButton"
        @click="showSearch"
        class="button-search flex-0 h-full rounded"
        :class="{
          'bg-interaction-light text-color-regular':
            lightTheme && search !== '',
          'text-white': lightTheme && search === '',
          'bg-interaction-light text-color-regular':
            !lightTheme && search !== '',
          'text-interaction-light': !lightTheme && search === ''
        }"
      >
        <icon-search />
      </button>
    </button-group>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import ButtonGroup from "./ButtonGroup";
import IconSearch from "@/components/icons/IconSearch";
import Vue from "vue";
import PseudoRandomNumberGenerator from "@/utils/PseudoRandomNumberGenerator";
import IconSortIncrease from "@/components/icons/IconSortIncrease";
import IconSortDecrease from "@/components/icons/IconSortDecrease";
import constants from "@/constants";
import FilterButton from "./filters/FilterButton.vue";

export default {
  name: "SortingButtons",
  components: {
    IconSortDecrease,
    IconSortIncrease,
    IconSearch,
    ButtonGroup,
    FilterButton
  },
  props: {
    candidateIds: {
      type: Array,
      required: true
    },
    lightTheme: {
      type: Boolean,
      default: false
    },
    showCustomButton: {
      type: Boolean,
      default: false
    },
    defaultSortBy: {
      type: String,
      default: null
    },
    defaultReverse: {
      type: Boolean,
      default: false
    },
    defaultSearch: {
      type: String,
      default: ""
    },
    initialState: {
      type: Object,
      default: null
    },
    allowSearch: {
      type: Boolean,
      default: true
    },
    showSearchButton: {
      type: Boolean,
      default: true
    },
    /**
     * @description Used for communication with the SortingButtonOverlays component.
     */
    overlayBus: {
      type: Vue,
      required: true
    }
  },
  data: () => ({
    ready: false, // only trigger status update after everything is initialized
    checkedParties: [],
    sortState: {
      sortBy: null,
      reverse: false,
      search: ""
    },
    search: ""
  }),
  computed: {
    ...mapGetters("candidate", ["candidate", "candidates"]),
    ...mapGetters("user", ["user"]),
    ...mapGetters("party", { allParties: "parties" }),
    parties() {
      return [
        ...new Set(
          this.candidateIds
            .map(entry => {
              return this.candidate(entry).partyId;
            })
            .concat(this.checkedParties)
            .filter(
              (partyId, index, self) =>
                partyId != null && self.indexOf(partyId) === index
            )
            .map(partyId => this.allParties[partyId])
            .filter(party => party != null)
            .sort((a, b) => {
              return a.name.localeCompare(b.name);
            })
        )
      ].sort();
    },
    initialSortStateOrDefault() {
      const initialState = this.initialState;
      return (
        (initialState != null ? initialState.sorting : null) || {
          sortBy: this.defaultSortBy,
          reverse: this.defaultReverse
        }
      );
    }
  },
  watch: {
    checkedParties: function() {
      this.sort(this.ready);
    },
    search() {
      this.sort(this.ready);
    }
  },
  mounted() {
    const initialState = this.initialState;
    this.checkedParties = (
      (initialState != null ? initialState.checkedParties : null) || []
    ).filter(partyId => this.allParties[partyId] != null);
    this.search = (initialState != null ? initialState.search : null) || "";
    this.sortState = this.initialSortStateOrDefault;
    this.overlayBus.$on("checkedparties", this.onCheckedPartiesChanged);

    if (this.allowSearch) {
      this.overlayBus.$on("search", this.onSearch);
    }

    if (this.search == null) {
      this.search = "";
    }
    this.sort(false);
    setTimeout(() => {
      this.ready = true;
    }, 50);
  },
  methods: {
    sort(emitStateUpdate = true) {
      const randomValues =
        this.sortState.sortBy === constants.SORT_RANDOM
          ? this.getRandomValues(this.candidateIds)
          : null;
      let list = [...this.candidateIds]
        .filter(id => {
          let c = this.candidate(id);
          if (this.checkedParties.length === 0) {
            return true;
          } else {
            return this.checkedParties.includes(c.partyId);
          }
        })
        .filter(id => {
          let c = this.candidate(id);
          if (this.search && this.allowSearch) {
            let name = `${c.firstName} ${c.lastName}`.toLowerCase();
            let search = this.search.toLowerCase();
            return name.indexOf(search) !== -1;
          } else {
            return true;
          }
        })
        .sort((a, b) => {
          if (this.sortState.sortBy === constants.SORT_RANDOM) {
            const randomA = randomValues[a];
            const randomB = randomValues[b];
            return randomA > randomB ? 1 : randomA < randomB ? -1 : 0;
          }
          let propertyA = this.candidate(a)[this.sortState.sortBy] || null;
          let propertyB = this.candidate(b)[this.sortState.sortBy] || null;
          if (propertyA > propertyB) return 1;
          if (propertyA < propertyB) return -1;
          return 0;
        });

      if (this.sortState.reverse) list.reverse();

      this.$emit("update:candidateIds", list);
      this.$emit("update:sortState", this.sortState);
      this.$emit("update:checkedParties", this.checkedParties);
      if (emitStateUpdate) {
        this.$emit("filterchanged", {
          sorting: this.sortState,
          checkedParties: this.checkedParties,
          search: this.search
        });
      }
    },
    sortBy(property, defaultReverse = false) {
      if (this.sortState.sortBy == property) {
        if (this.sortState.reverse === defaultReverse) {
          this.sortState.reverse = !defaultReverse;
        } else {
          this.sortState.sortBy = this.defaultSortBy;
          this.sortState.reverse = this.defaultReverse;
        }
      } else {
        this.sortState.sortBy = property;
        this.sortState.reverse = defaultReverse;
      }

      this.sort(this.ready);
    },
    showPartySelection() {
      this.overlayBus.$emit("parties", {
        parties: this.parties,
        checkedParties: this.checkedParties
      });
    },
    showSearch() {
      this.overlayBus.$emit("showSearch", this.search);
    },
    onCheckedPartiesChanged(checkedParties) {
      this.checkedParties = checkedParties;
    },
    onSearch(search) {
      this.search = search;
    },
    getRandomValues(candidateIds) {
      const randomSeed = this.user.email;
      const rng = new PseudoRandomNumberGenerator(randomSeed);
      return Object.fromEntries(
        candidateIds.sort().map(c => [c, rng.getValue(c * 10, c * 43, c * -32)])
      );
    }
  }
};
</script>
